import React, { useEffect, useState } from 'react'
import { Card, CardBody, CardHeader } from '../../../../_metronic/_partials/controls'
import {
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  FormGroup,
  FormLabel,
  FormControl,
  IconButton,
} from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/Delete'
import { useHistory, useParams } from 'react-router-dom'
import { getDepartmentsAdmin, updateDepartment, postDepartment } from '../../../../api/department'
import { useSkeleton } from '../../../hooks/useSkeleton'
import { alertError, alertSuccess } from '../../../../utils/logger'

const DAY_OPTIONS = [
  { value: 1, label: 'Lunes' },
  { value: 2, label: 'Martes' },
  { value: 3, label: 'Miércoles' },
  { value: 4, label: 'Jueves' },
  { value: 5, label: 'Viernes' },
  { value: 6, label: 'Sábado' },
  { value: 0, label: 'Domingo' },
]

const DEFAULT_DAYS = [1, 2, 3, 4, 5]

const EMPTY_DEPT = {
  name: '',
  slotDurationMinutes: 30,
  workStart: '09:00',
  workEnd: '14:00',
  workDays: DEFAULT_DAYS,
  sites: [],
}

function emptySite(fallback) {
  return {
    name: '',
    useGenericSchedule: true,
    workStart: fallback.workStart || '09:00',
    workEnd: fallback.workEnd || '14:00',
    workDays: [...(fallback.workDays || DEFAULT_DAYS)],
  }
}

function normalizeLoadedSites(sites, fallback) {
  if (!Array.isArray(sites)) return []
  return sites
    .map((raw) => {
      if (typeof raw === 'string') {
        const name = raw.trim()
        if (!name) return null
        return {
          name,
          useGenericSchedule: true,
          workStart: fallback.workStart,
          workEnd: fallback.workEnd,
          workDays: [...fallback.workDays],
        }
      }
      const name = String(raw?.name || '').trim()
      if (!name) return null
      const useGeneric = raw.useGenericSchedule !== false
      return {
        name,
        useGenericSchedule: useGeneric,
        workStart: raw.workStart || fallback.workStart,
        workEnd: raw.workEnd || fallback.workEnd,
        workDays:
          Array.isArray(raw.workDays) && raw.workDays.length
            ? raw.workDays
            : [...fallback.workDays],
      }
    })
    .filter(Boolean)
}

function WorkDaysPicker({ days, onToggle }) {
  return (
    <FormGroup row>
      {DAY_OPTIONS.map((opt) => (
        <FormControlLabel
          key={opt.value}
          control={
            <Checkbox
              checked={(days || []).includes(opt.value)}
              onChange={() => onToggle(opt.value)}
              color="primary"
            />
          }
          label={opt.label}
        />
      ))}
    </FormGroup>
  )
}

export default function EditDepartmentPage() {
  const [dept, setDept] = useState(null)
  const [newSite, setNewSite] = useState('')
  const deptId = useParams().id
  const history = useHistory()
  const { isLoading: isLoadingData, disableLoading: disableLoadingData, ContentSkeleton } = useSkeleton()

  useEffect(() => {
    if (!deptId) {
      setDept({ ...EMPTY_DEPT, workDays: [...DEFAULT_DAYS], sites: [] })
      disableLoadingData()
      return
    }
    getDepartmentsAdmin()
      .then((res) => {
        if (res.status === 200) {
          const found = res.data.find((d) => d._id === deptId)
          if (!found) {
            history.push('/departments')
            return
          }
          const workDays = found.workDays && found.workDays.length ? found.workDays : [...DEFAULT_DAYS]
          const workStart = found.workStart || '09:00'
          const workEnd = found.workEnd || '14:00'
          setDept({
            ...found,
            name: found.name || '',
            workDays,
            slotDurationMinutes: found.slotDurationMinutes || 30,
            workStart,
            workEnd,
            sites: normalizeLoadedSites(found.sites, { workStart, workEnd, workDays }),
          })
          disableLoadingData()
        }
      })
      .catch((error) => {
        alertError({ error, customMessage: 'No se pudo cargar el servicio.' })
        history.push('/departments')
      })
  }, [deptId, disableLoadingData, history])

  function patch(field, value) {
    setDept((prev) => ({ ...prev, [field]: value }))
  }

  function toggleDay(day) {
    setDept((prev) => {
      const days = prev.workDays || []
      const next = days.includes(day)
        ? days.filter((d) => d !== day)
        : [...days, day].sort((a, b) => a - b)
      return { ...prev, workDays: next }
    })
  }

  function addSite() {
    const value = String(newSite || '').trim()
    if (!value) return
    setDept((prev) => {
      const sites = prev.sites || []
      if (sites.some((s) => s.name.toLowerCase() === value.toLowerCase())) return prev
      return {
        ...prev,
        sites: [...sites, { ...emptySite(prev), name: value }],
      }
    })
    setNewSite('')
  }

  function patchSite(index, field, value) {
    setDept((prev) => ({
      ...prev,
      sites: (prev.sites || []).map((s, i) =>
        i === index ? { ...s, [field]: value } : s,
      ),
    }))
  }

  function toggleSiteDay(index, day) {
    setDept((prev) => ({
      ...prev,
      sites: (prev.sites || []).map((s, i) => {
        if (i !== index) return s
        const days = s.workDays || []
        const next = days.includes(day)
          ? days.filter((d) => d !== day)
          : [...days, day].sort((a, b) => a - b)
        return { ...s, workDays: next }
      }),
    }))
  }

  function removeSite(index) {
    setDept((prev) => ({
      ...prev,
      sites: (prev.sites || []).filter((_, i) => i !== index),
    }))
  }

  function save() {
    const name = String(dept.name || '').trim()
    if (!name) {
      alertError({ customMessage: 'El nombre del servicio es obligatorio.' })
      return
    }
    const sites = (dept.sites || [])
      .map((s) => {
        const siteName = String(s.name || '').trim()
        if (!siteName) return null
        const useGeneric = s.useGenericSchedule !== false
        const item = { name: siteName, useGenericSchedule: useGeneric }
        if (!useGeneric) {
          item.workStart = s.workStart || dept.workStart
          item.workEnd = s.workEnd || dept.workEnd
          item.workDays =
            Array.isArray(s.workDays) && s.workDays.length
              ? s.workDays
              : dept.workDays
        }
        return item
      })
      .filter(Boolean)
    const payload = {
      name,
      slotDurationMinutes: Number(dept.slotDurationMinutes) || 30,
      workStart: dept.workStart,
      workEnd: dept.workEnd,
      workDays: dept.workDays,
      sites,
    }
    const request = deptId ? updateDepartment(deptId, payload) : postDepartment(payload)
    request
      .then((res) => {
        if (res.status === 200 || res.status === 201) {
          alertSuccess({
            title: 'Guardado',
            customMessage: deptId ? 'Horario actualizado.' : 'Servicio creado.',
          })
          history.push('/departments')
        }
      })
      .catch((error) => {
        alertError({
          error,
          customMessage: deptId ? 'No se pudo guardar el horario.' : 'No se pudo crear el servicio.',
        })
      })
  }

  if (isLoadingData || !dept) return <ContentSkeleton />

  return (
    <>
      <Card>
        <CardHeader title={deptId ? `Horario: ${dept.name}` : 'Nuevo servicio'} />
        <CardBody>
          <TextField
            label="Nombre del servicio"
            value={dept.name}
            onChange={(e) => patch('name', e.target.value)}
            margin="normal"
            variant="outlined"
            fullWidth
            required
          />
          <TextField
            label="Duración del slot (minutos)"
            type="number"
            value={dept.slotDurationMinutes}
            onChange={(e) => patch('slotDurationMinutes', e.target.value)}
            margin="normal"
            variant="outlined"
            fullWidth
            inputProps={{ min: 15, max: 120, step: 15 }}
            helperText="La duración del hueco es la misma para todos los centros de este servicio."
          />
          <FormControl component="fieldset" margin="normal" fullWidth>
            <FormLabel component="legend">Horario genérico</FormLabel>
            <p style={{ margin: '4px 0 8px', color: '#7E8299', fontSize: 13 }}>
              Se usa cuando un centro no tiene horario propio, o si el servicio no tiene centros asignados.
            </p>
          </FormControl>
          <TextField
            label="Hora inicio"
            type="time"
            value={dept.workStart}
            onChange={(e) => patch('workStart', e.target.value)}
            margin="normal"
            variant="outlined"
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Hora fin"
            type="time"
            value={dept.workEnd}
            onChange={(e) => patch('workEnd', e.target.value)}
            margin="normal"
            variant="outlined"
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <FormControl component="fieldset" margin="normal" fullWidth>
            <FormLabel component="legend">Días laborables</FormLabel>
            <WorkDaysPicker days={dept.workDays} onToggle={toggleDay} />
          </FormControl>

          <FormControl component="fieldset" margin="normal" fullWidth>
            <FormLabel component="legend">Sitios / centros (opcional)</FormLabel>
            <p style={{ margin: '4px 0 12px', color: '#7E8299', fontSize: 13 }}>
              Si defines centros, el ciudadano deberá elegir uno al pedir cita de este servicio.
              Cada centro puede usar el horario genérico o uno propio. Si no hay ninguno, no se pide sitio.
            </p>
            {(dept.sites || []).map((site, index) => (
              <div
                key={`site-${index}`}
                style={{
                  border: '1px solid #EAE6DF',
                  borderRadius: 12,
                  padding: 16,
                  marginBottom: 12,
                  background: '#FDFCF9',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <TextField
                    label={`Centro ${index + 1}`}
                    value={site.name}
                    onChange={(e) => patchSite(index, 'name', e.target.value)}
                    variant="outlined"
                    fullWidth
                    size="small"
                  />
                  <IconButton
                    aria-label="Eliminar centro"
                    onClick={() => removeSite(index)}
                    size="small"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </div>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={site.useGenericSchedule !== false}
                      onChange={(e) =>
                        patchSite(index, 'useGenericSchedule', e.target.checked)
                      }
                      color="primary"
                    />
                  }
                  label="Usar horario genérico del servicio"
                />
                {site.useGenericSchedule === false ? (
                  <div style={{ marginTop: 8 }}>
                    <TextField
                      label="Hora inicio"
                      type="time"
                      value={site.workStart || dept.workStart}
                      onChange={(e) => patchSite(index, 'workStart', e.target.value)}
                      margin="normal"
                      variant="outlined"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      label="Hora fin"
                      type="time"
                      value={site.workEnd || dept.workEnd}
                      onChange={(e) => patchSite(index, 'workEnd', e.target.value)}
                      margin="normal"
                      variant="outlined"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                    <FormControl component="fieldset" margin="normal" fullWidth>
                      <FormLabel component="legend">Días laborables</FormLabel>
                      <WorkDaysPicker
                        days={site.workDays}
                        onToggle={(day) => toggleSiteDay(index, day)}
                      />
                    </FormControl>
                  </div>
                ) : null}
              </div>
            ))}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
              <TextField
                label="Nuevo centro"
                value={newSite}
                onChange={(e) => setNewSite(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addSite()
                  }
                }}
                variant="outlined"
                fullWidth
                size="small"
                placeholder="Ej. SSB de Luna"
              />
              <Button onClick={addSite} variant="outlined" color="primary" style={{ whiteSpace: 'nowrap' }}>
                Añadir
              </Button>
            </div>
          </FormControl>
        </CardBody>
      </Card>
      <Button onClick={() => history.push('/departments')} variant="outlined" style={{ marginRight: 20 }}>
        Volver
      </Button>
      <Button onClick={save} variant="outlined" color="primary">
        Guardar
      </Button>
    </>
  )
}
